import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  PropertyDto,
  PropertyModerationDto,
  PropertyModerationEntryDto,
  PropertyModerationStatus,
  UserSummaryDto,
} from '@workspace/data-types';
import { AlertSnackBarService } from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, finalize, tap } from 'rxjs';

@Component({
  selector: 'etxe-property-moderation-display',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    MatButtonModule,
    MatMenuModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './property-moderation-display.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyModerationDisplayComponent {
  readonly ModerationStatus = PropertyModerationStatus;

  private readonly api = inject(ApiService);
  private readonly alertSnackbar = inject(AlertSnackBarService);
  private readonly destroyRef = inject(DestroyRef);

  property = input.required<PropertyDto>();
  propertyUpdated = output<PropertyDto>();

  loadingEntryId = signal<string | null>(null);

  moderation = computed<PropertyModerationDto | undefined>(() => this.property()?.moderation);

  history = computed<PropertyModerationEntryDto[]>(() => this.moderation()?.history || []);

  todoCount = computed<number>(
    () => this.history().filter((e) => !e.status || e.status === PropertyModerationStatus.TODO).length,
  );

  flaggedCount = computed<number>(
    () => this.history().filter((e) => e.status === PropertyModerationStatus.FLAGGED).length,
  );

  falsePositiveCount = computed<number>(
    () => this.history().filter((e) => e.status === PropertyModerationStatus.FALSE_POSITIVE).length,
  );

  hasFlag = computed<boolean>(() => this.todoCount() > 0 || this.flaggedCount() > 0);

  updateEntryStatus(
    entry: PropertyModerationEntryDto,
    status: PropertyModerationStatus,
    note?: string,
  ): void {
    const propId = this.property()._id;
    const entryId = entry._id;
    if (!propId || !entryId) return;

    this.loadingEntryId.set(entryId);
    this.api
      .business()
      .etxe()
      .admin()
      .properties()
      .patchModerationEntryStatus(propId, entryId, status, note)
      .pipe(
        tap((updatedProperty) => {
          const statusLabel =
            status === PropertyModerationStatus.FALSE_POSITIVE
              ? 'Faux positif'
              : status === PropertyModerationStatus.FLAGGED
                ? 'Signalement confirmé'
                : 'À traiter';

          this.alertSnackbar.success({
            title: 'Statut mis à jour',
            message: `Le signalement a été classé comme : ${statusLabel}.`,
          });
          this.propertyUpdated.emit(updatedProperty);
        }),
        catchError((err) => {
          console.error('Error updating moderation entry status:', err);
          this.alertSnackbar.error({
            title: 'Erreur',
            message: 'Impossible de mettre à jour le statut du signalement.',
          });
          return EMPTY;
        }),
        finalize(() => this.loadingEntryId.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  getReviewerName(entry: PropertyModerationEntryDto): string | null {
    if (entry.reviewedBy && typeof entry.reviewedBy === 'object' && 'identity' in entry.reviewedBy) {
      const user = entry.reviewedBy as UserSummaryDto;
      if (user.identity?.firstName || user.identity?.lastName) {
        return `${user.identity?.firstName || ''} ${user.identity?.lastName || ''}`.trim();
      }
    }
    return null;
  }

  getFieldLabel(field: string): string {
    switch (field) {
      case 'presentation.title':
        return "Titre de l'annonce";
      case 'presentation.description':
        return 'Description du bien';
      case 'financials.precisions':
        return 'Précisions financières';
      default:
        return field;
    }
  }

  getReasonLabel(reason: string): string {
    switch (reason) {
      case 'phone':
        return 'Numéro de téléphone';
      case 'email':
        return 'Adresse email';
      case 'url':
        return 'Lien URL';
      case 'social':
        return 'Réseau social';
      default:
        return reason;
    }
  }
}
